/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CollectionComparator, valueComparator } from '@gh/core-util';

import { ColDef, ColGroupDef, IRowNode, RowDragMoveEvent } from 'ag-grid-community';
import { eq, isNil } from 'lodash';

export const MIN_COLUMN_WIDTH = 65;
export const DEFAULT_COLUMN_WIDTH = 200;

export type ParamsWithData<P> = P & { data: any };

export const equalsByKey =
  (item: any, key: string): ((row: any) => boolean) => (row: any): boolean => eq(row[key], item[key]);

/**
 * Converts given entity comparator to ag-grid ready comparator.
 * Result comparator can be assigned to column definition.
 * Although result comparator is assigned to column definition projecting single entity field,
 * given <code>comparator</code> accepts full entity stored in the row.
 */
export const gridCollectionComparator = (comparator: CollectionComparator<any>) =>
  (_1: any, _2: any, nodeA?: IRowNode, nodeB?: IRowNode, _isDescending?: boolean) =>
    comparator(nodeA!.data, nodeB!.data);

/**
 * Converts given value comparator to ag-grid ready comparator.
 * Result comparator can be assigned to column definition.
 * Unlike {@link gridCollectionComparator} <code>valueComparator</code> accepts cell value (formatted or raw).
 */
export const gridValueCollectionComparator = (comparator: CollectionComparator<any>) =>
  (leftValue: any, rightValue: any) => comparator(leftValue, rightValue);

/**
 * Converts given value comparator to ag-grid ready comparator for editable cell.
 * Result comparator can be assigned to column definition.
 * Unlike {@link gridCollectionComparator} <code>valueComparator</code> accepts cell value (formatted or raw).
 */
export const gridEditableValueCollectionComparator = (comparator: CollectionComparator<any>) =>
  (leftValue: any, rightValue: any) => comparator(leftValue.value, rightValue.value);

/**
 * Converts given value comparator to ag-grid ready comparator.
 * Result comparator can be assigned to column definition.
 * Unlike {@link gridCollectionComparator} <code>valueComparator</code> accepts cell value (formatted or raw).
 *
 * Unlike {@link gridValueCollectionComparator} this method allows to pass <code>beforeComparator</code> and
 * <code>afterComparator</code>. <code>valueComparator</code> is called only if <code>beforeComparator</code>
 * is not specified or returned <code>0</code>. <code>afterComparator</code> is called only if
 * <code>valueComparator</code> returned 0. <code>beforeComparator</code> and <code>afterComparator</code>
 * accepts full entity stored in the row.
 * This method allows to define complex sort logic, where sort order is defined by the following rule:
 * <code>presort by entity => sort by value if entity equals => sort by entity again if value equals</code>
 */
export const gridAroundValueCollectionComparator = (comparator: CollectionComparator<any>,
                                                    beforeComparator?: CollectionComparator<any>,
                                                    afterComparator?: CollectionComparator<any>) =>
  (leftValue: any, rightValue: any, nodeLeft?: IRowNode, nodeRight?: IRowNode) => {
    const byBefore = beforeComparator ? beforeComparator(nodeLeft!.data, nodeRight!.data) : 0;
    const byValue = byBefore === 0 ? comparator(leftValue, rightValue) : byBefore;
    return byValue === 0 && afterComparator ? afterComparator(nodeLeft!.data, nodeRight!.data) : 0;
  };

export function safeGridOptionCallback<P, R>(callback: (params: ParamsWithData<P>) => R,
                                             defaultResult?: R): (params: ParamsWithData<P>) => R;
export function safeGridOptionCallback(callback: (params: any) => any,
                                       defaultResult: any = void 0): (params: ParamsWithData<any>) => any {
  return (params) => params.data ? callback(params) : defaultResult;
}

export function rowDragMoveEventArrayFactory<T>(move: (from: T, to: T) => void): (event: RowDragMoveEvent) => void {
  return (event) => {
    const { node: movingNode, overNode } = event;

    const rowNeedsToMove = movingNode !== overNode;

    if (rowNeedsToMove && movingNode && overNode) {
      move(movingNode.data, overNode.data);
    }
  };
}

/**
 * Case insensitive comparator for grid column values
 */
export const caseInsensitiveGridComparator = gridValueCollectionComparator(valueComparator((str) => str.toLowerCase()));

export function isColDef(col: ColDef | ColGroupDef): col is ColDef {
  return isNil(col['children']);
}
